import { useMemo } from 'react';

import { useFlags } from '~/hooks/utils/launchDarklyInterceptor';

/**
 * Flags that are temporary and should be removed after a certain date.
 *
 * If you need are adding a permanent flag, add it to `useFlags` instead.
 */
export type TemporaryFeatureFlags = {
  /**
   * Added on October 22, 2025
   * This flag enables performance analytics for the workspace. Should be replaced by addOn
   */
  performanceAnalytics: boolean;

  /**
   * Added on December 16, 2025
   * This flag enables select all functionality for bulk operations
   */
  selectAllEnabled: boolean;

  /**
   * Added on January 29, 2026
   * This flag enables the canvas editor features
   */
  generativeCanvasEnabled: boolean;
  /**
   * Added on February 23, 2026
   * This flag enables CSV download of an asset's custom field data
   * @TODO(PLA-955): Remove after rollout
   */
  customFieldsCsvDownload: boolean;
  /**
   * Added on March 16, 2026
   * This flag enables credit usage stats for legacy plans
   * @TODO(AI-1041): Remove after rollout
   */
  creditUsageEnabledForLegacyPlans: boolean;

  /**
   * Added on March 19, 2026
   * This flag enables the canvas edit elements feature
   * @TODO(AI-1157): Remove after rollout
   */
  canvasEditElementsEnabled: boolean;

  /**
   * Added on April 27, 2026
   * This flag enables the skills UI
   * @TODO(AI-1523): Remove after rollout
   */
  enableSkillsUi: boolean;

  /**
   * Added on October 13, 2026
   * Gates workspace agent composer `/` slash skills (menu, placeholder, auto-format) and the
   * workspace-agent full-page skills experience (organization/canvas tabs, detail routes).
   * Requires `enableSkillsUi`. Missing values are off.
   * @TODO(WORK-817): Remove composer gate after rollout
   * @TODO(WORK-836): Remove full-page gate after rollout
   */
  workspaceAgentSkillsEnabled: boolean;

  /**
   * Added on October 9, 2026
   * Gates the workspace scheduled automations management UI.
   * @TODO(WORK-802): Remove after rollout
   */
  automationsEnabled: boolean;

  /**
   * Added on April 30, 2026
   * This flag enables the team invite step experiment in onboarding
   * @TODO(COR-1823): Remove after experiment
   */
  onboardingTeamInviteStepEnabled: boolean;

  /**
   * Added on June 8, 2026
   * Internal-only guard for the static "OAuth PoC" card on the account
   * Integrations page. Kept as its own flag so that, now that the public
   * connected-apps surface is always on, the internal PoC card is still never
   * exposed in production.
   * Target to internal users only in LaunchDarkly.
   * @TODO(FOUND-134): Remove after the OAuth PoC is no longer needed
   */
  oauthPocCardEnabled: boolean;

  /**
   * Added on May 18, 2026
   * Gates the LinkedIn integration card on the account Integrations page.
   * @TODO(APP-438): Remove after rollout
   */
  linkedInIntegrationEnabled: boolean;

  /**
   * Added on June 18, 2026
   * This flag enables adaptive upload concurrency. When disabled, uploads use
   * the legacy fixed max concurrency.
   * @TODO(PLA-1252): Remove after adaptive upload concurrency rollout
   */
  adaptiveConcurrencyEnabled: boolean;

  /**
   * Added on June 25, 2026
   * This flag enables the `fal-ai/flux-2-pro/outpaint` model override for the
   * agent's `outpaint` capability, injected into the agent's modelConfig — it
   * governs Canvas only. When off, the agent defaults to `fal-ai/bria/expand`.
   * The Asset Modal's "Extend background" runs through the AssetTransformer,
   * which this flag does not reach.
   * @TODO(PER-109): Remove after rollout
   */
  flux2ProOutpaintingEnabled: boolean;

  /**
   * Added on July 7, 2026
   * This flag enables the "Exact size" mode in the batch export modal (custom
   * pixel dimensions + crop anchor). When off, the modal only offers Scale.
   * @TODO(PER-180): Remove after rollout
   */
  exactSizeExportEnabled: boolean;

  /**
   * Added on August 20, 2026
   * Gates the 2026 workspace UI redesign (white page chrome, thin gray frame).
   * Missing until flags load — `useTemporaryFeatureFlags` treats that as on.
   * Only an explicit `false` (LaunchDarkly or a dev override) uses classic chrome.
   * @TODO(WORK-245): Remove after rollout
   */
  ui2026: boolean;

  /**
   * Added on September 5, 2026
   * Gates the Frame.io OAuth importer for non-internal workspaces. Internal
   * users always see it via INTERNAL_IMPORT_SOURCES. Create this flag in
   * LaunchDarkly with the same key before customer rollout. Missing values are
   * treated as off.
   * @TODO(FOUND-695): Remove after rollout (or move to FeatureFlags if it stays an enterprise gate)
   */
  showFrameIoImportSource: boolean;

  /**
   * Added on September 8, 2026
   * Gates the saved sizes and sets sections/affordances in the image Social
   * variants picker, video picker, asset-details crop, and Export As flows.
   * @TODO(PLA-1728): Remove after rollout
   */
  savedSizesEnabled: boolean;

  /**
   * Added on September 21, 2026
   * Gates the SCIM group→role mapping table in workspace security settings.
   * The admin API exists before SCIM `/Groups`, so saved mappings stay
   * `awaiting` until that lands — hide the panel from customers until then.
   * @TODO(FOUND-779): Remove after SCIM group provisioning ships
   */
  scimGroupRoleMapping: boolean;

  /**
   * Added on September 25, 2026
   * Variant-count pills on canvas Animate and the agent composer.
   * @TODO(PER-316): Remove after rollout
   */
  canvasAnimateVariantsEnabled: boolean;

  /**
   * Added on September 22, 2026
   * Gates in-product announcement surfaces in the private web app.
   * @TODO(PER-337): Remove after rollout
   */
  announcementsEnabled: boolean;

  /**
   * Added on October 11, 2026
   * Gates ADS color tokens on components that have been migrated off the legacy
   * color scales (they carry `ads:` classes). Missing values are off.
   * @TODO(ENG-1363): Remove after rollout
   */
  adsColors: boolean;

  /**
   * Added on October 15, 2026
   * Gates the "Resize for [set name]" item in the image Edit in Canvas submenu
   * (experiment, split by workspace). Missing values are off.
   * @TODO(PER-469): Remove after experiment
   */
  editInCanvasResizeForSetEnabled: boolean;

  /**
   * Added on October 23, 2026
   * Gates the HTML5 drag backend that still completes a drop when `dragend` fires first.
   * Missing values are off.
   */
  dndFixEnabled: boolean;

  /**
   * Added on October 29, 2026
   * Gates the empty-thread prompt-card carousel and the three-chip suggestion list.
   * Missing values are off.
   * @TODO(WORK-1025): Remove after rollout
   */
  improvedAgentNullState: boolean;
};

export const useTemporaryFeatureFlags = (): TemporaryFeatureFlags => {
  const flags = useFlags<TemporaryFeatureFlags>();

  // LaunchDarkly omits keys until the client has flags. ui2026 stays on through that gap
  // so the workspace does not paint classic chrome and then swap. Explicit false still wins.
  return useMemo(
    () => ({
      ...flags,
      ui2026: flags.ui2026 !== false,
      adsColors: flags.adsColors === true,
      editInCanvasResizeForSetEnabled: flags.editInCanvasResizeForSetEnabled === true,
      dndFixEnabled: flags.dndFixEnabled === true,
      improvedAgentNullState: flags.improvedAgentNullState === true,
    }),
    [flags],
  );
};
